import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowDown, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { ContactModal } from '@/components/modals/ContactModal';

import { useAuth } from '@/contexts/AuthContext';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';
import { LanguageToggle } from '@/components/ui/LanguageToggle';

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPurpose, setModalPurpose] = useState<string>();

  const { clearAuthState } = useAuth();
  const navigate = useNavigate();

  const {
    getNavigationText,
    getPricingHero,
    getPricingTier,
    getPricingBilling,
    getPricingComparison,
    getPricingFAQ,
    getPricingCTA,
    getPricingAccessibility,
    getLandingFooter,
    getPricingFAQItems,
    getTierRealFeatures,
    getComparisonTableRows,
  } = useMarketingTranslations();

  // Clear any stale authentication state when visiting pricing page
  // React.useEffect(() => {
  //   clearAuthState()
  // }, []) // Empty dependency array - only run once on mount

  const handleGetStarted = async (plan: string) => {
    const billing = billingCycle === 'annual' ? 'annual' : 'monthly';

    // All plans now go through the request organization page
    // which handles Stripe integration
    navigate(`/request-organization?plan=${plan}&billing=${billing}`);
  };

  const handleContactSales = () => {
    setModalPurpose('Speak with Sales');
    setIsModalOpen(true);
  };

  const handleFreeTrial = () => {
    setModalPurpose('Request a Free Trial');
    setIsModalOpen(true);
  };

  // Structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'KernLogic PIM Platform',
    description: 'Enterprise PIM platform with transparent pricing tiers for every business scale',
    url: 'https://kernlogic.com/marketing/pricing',
    offers: [
      {
        '@type': 'Offer',
        name: 'Basic',
        price: '49',
        priceCurrency: 'EUR',
        billingIncrement: 'Monthly',
        description: 'Up to 100 SKUs, 500 MB Storage, 1 User',
      },
      {
        '@type': 'Offer',
        name: 'Pro',
        price: '299',
        priceCurrency: 'EUR',
        billingIncrement: 'Monthly',
        description: 'Up to 2,000 SKUs, 30 GB Storage, 5 Users',
      },
      {
        '@type': 'Offer',
        name: 'Business',
        price: '739',
        priceCurrency: 'EUR',
        billingIncrement: 'Monthly',
        description: 'Up to 10,000 SKUs, 100 GB Storage, 15 Users',
      },
      {
        '@type': 'Offer',
        name: 'Enterprise',
        price: '1470',
        priceCurrency: 'EUR',
        billingIncrement: 'Monthly',
        description: 'Up to 100,000 SKUs, 500 GB Storage, Unlimited Users',
      },
    ],
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://kernlogic.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Pricing',
        item: 'https://kernlogic.com/marketing/pricing',
      },
    ],
  };

  const getPricing = (basePrice: number) => {
    // Specific annual pricing with 15% discount
    const annualPricing: {
      [key: number]: { monthly: number; yearly: number; savings: number };
    } = {
      49: { monthly: 42, yearly: 504, savings: 84 },
      299: { monthly: 254, yearly: 3048, savings: 540 },
      739: { monthly: 628, yearly: 7536, savings: 1332 },
      1470: { monthly: 1250, yearly: 15000, savings: 2640 },
    };

    if (billingCycle === 'annual') {
      const pricing = annualPricing[basePrice];
      if (pricing) {
        return {
          price: pricing.monthly,
          period: getPricingTier('basic', 'periodAnnual'),
          savings: getPricingBilling('savings', { amount: pricing.savings }),
        };
      }
    }
    return {
      price: basePrice,
      period: getPricingTier('basic', 'period'),
      savings: null,
    };
  };

  // Get structured data from translations
  const faqItems = getPricingFAQItems();
  const comparisonRows = getComparisonTableRows();

  return (
    <main
      role="main"
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      data-scroll-container
    >
      {/* SEO Meta Tags */}
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>

      {/* Navigation */}
      <motion.header
        className="sticky top-0 z-50 border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm"
        role="banner"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <nav
          className="container mx-auto px-6 py-4"
          role="navigation"
          aria-label={getNavigationText('ariaLabel')}
        >
          <div className="flex items-center justify-between">
            <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
              <Link to="/marketing" className="group flex items-center">
                <motion.img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt={getNavigationText('brand')}
                  className="size-12 transition-transform group-hover:scale-105"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
                <span className="ml-3 text-xl font-bold leading-[1.2] text-white transition-colors group-hover:text-primary-300">
                  {getNavigationText('brand')}
                </span>
              </Link>
            </motion.div>
            <div className="flex items-center space-x-6">
              <motion.div
                className="hidden items-center space-x-6 md:flex"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/marketing/product"
                    className="rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                    aria-current="page"
                  >
                    {getNavigationText('product')}
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/marketing/pricing"
                    className="rounded-md px-3 py-2 text-sm font-medium text-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                  >
                    {getNavigationText('pricing')}
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1, y: -2 }} transition={{ duration: 0.2 }}>
                  <Link
                    to="/login"
                    className="rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    tabIndex={0}
                  >
                    {getNavigationText('login')}
                  </Link>
                </motion.div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <LanguageToggle />
              </motion.div>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Hero/Intro */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-10 pb-32 text-center sm:px-8 md:py-24 lg:py-28"
        aria-labelledby="hero-title"
      >
        {/* Ambient Radial Glow */}
        <div className="absolute -top-32 left-1/2 z-0 size-[500px] -translate-x-1/2 rounded-full bg-primary-500/10 opacity-70 blur-3xl" />

        {/* Background Illustration */}
        <motion.div
          className="pointer-events-none absolute left-1/2 top-[13%] z-0 hidden -translate-x-1/2 -translate-y-[40%] select-none opacity-[0.04] grayscale sm:block"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.08, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.5 }}
        >
          <motion.img
            src="/Data Reports.svg"
            alt=""
            className="z-10 h-auto w-[400px] max-w-full select-none object-contain contrast-100 grayscale lg:w-[500px] xl:w-[600px]"
            animate={{ y: [0, -6, 0] }}
            transition={{
              duration: 16,
              ease: 'easeInOut',
              repeat: Infinity,
              delay: 2,
            }}
          />
        </motion.div>

        <div className="container mx-auto">
          <div className="relative z-10 mx-auto max-w-4xl">
            {/* Badge */}
            <motion.div
              className="inline-block"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="mb-4 inline-block rounded-full border border-primary-400/20 bg-primary-400/10 px-4 py-1 text-sm font-medium text-primary-300">
                {getPricingHero('badge')}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              id="hero-title"
              className="mb-6 text-4xl font-bold tracking-tight text-white sm:text-5xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="bg-gradient-to-r from-white via-slate-100 to-slate-200 bg-clip-text text-transparent">
                {getPricingHero('title')}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-300 sm:text-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {getPricingHero('subtitle')}
            </motion.p>

            {/* Highlight Line */}
            <motion.p
              className="mt-3 text-sm italic text-slate-400"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {getPricingHero('highlight')}
            </motion.p>

            {/* Scroll Down Arrow */}
            <motion.div
              className="mb-2 mt-8 flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <button
                onClick={() => {
                  const el = document.getElementById('pricing-plans');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                aria-label="Scroll to pricing"
                className="group rounded-full p-2 transition-transform duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                <ArrowDown className="size-6 animate-bounce text-primary-400 transition-colors duration-300 group-hover:text-primary-300" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Tiers Grid */}
      <section
        id="pricing-plans"
        className="scroll-mt-24 bg-slate-800/50 py-24"
        aria-labelledby="pricing-title"
      >
        <div className="container mx-auto px-6">
          {/* Billing Cycle Toggle */}
          <motion.div
            className="mb-12 flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center space-x-4 rounded-full border border-slate-700/50 bg-slate-800/80 p-1 backdrop-blur-sm">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`rounded-full px-6 py-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                  billingCycle === 'monthly'
                    ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg'
                    : 'text-slate-400 hover:text-slate-300'
                }`}
                aria-pressed={billingCycle === 'monthly'}
              >
                {getPricingBilling('monthly')}
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`rounded-full px-6 py-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                  billingCycle === 'annual'
                    ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg'
                    : 'text-slate-400 hover:text-slate-300'
                }`}
                aria-pressed={billingCycle === 'annual'}
              >
                {getPricingBilling('annualDiscount')}
              </button>
            </div>
          </motion.div>

          <motion.div
            className="grid gap-8 md:grid-cols-1 lg:grid-cols-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Basic Tier */}
            <motion.div
              className="relative flex flex-col rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 shadow-xl backdrop-blur-sm transition-all duration-300 hover:border-primary-500/30 hover:shadow-2xl hover:shadow-primary-500/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="mb-6">
                <h3 className="mb-4 text-2xl font-semibold leading-[1.3] text-white">
                  {getPricingTier('basic', 'name')}
                </h3>
                <div className="mb-2">
                  <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-4xl font-bold text-transparent">
                    {getPricingTier('basic', 'price', {
                      price: getPricing(299).price,
                    })}
                  </span>
                  <span className="text-lg font-medium text-slate-400">
                    {getPricing(299).period}
                  </span>
                </div>
                {getPricing(299).savings && (
                  <p className="mb-3 text-sm font-medium text-green-400">
                    {getPricing(299).savings}
                  </p>
                )}
                <p className="text-sm text-slate-400">{getPricingTier('basic', 'description')}</p>
              </div>
              <ul className="mb-8 space-y-4 text-slate-300" role="list">
                {getTierRealFeatures('basic').map((feature, i) => (
                  <li key={i} className="flex items-center">
                    <CheckCircle2 className="mr-3 size-5 text-green-400" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleGetStarted('basic')}
                size="lg"
                className="mt-auto bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg hover:from-primary-700 hover:to-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {getPricingTier('basic', 'button')}
              </Button>
            </motion.div>

            {/* Business Tier (Most Popular) */}
            <motion.div
              className="relative flex scale-105 flex-col rounded-2xl border-2 border-primary-500/50 bg-gradient-to-br from-slate-800/90 to-slate-900/90 p-8 shadow-2xl backdrop-blur-sm transition-all duration-300 hover:shadow-2xl hover:shadow-primary-500/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="rounded-full bg-gradient-to-r from-primary-600 to-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-lg">
                  {getPricingTier('business', 'badgeShort')}
                </span>
              </div>
              <div className="mb-6 mt-2">
                <h3 className="mb-4 text-2xl font-semibold leading-[1.3] text-white">
                  {getPricingTier('business', 'name')}
                </h3>
                <div className="mb-2">
                  <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-4xl font-bold text-transparent">
                    {getPricingTier('business', 'price', {
                      price: getPricing(739).price,
                    })}
                  </span>
                  <span className="text-lg font-medium text-slate-400">
                    {getPricing(739).period}
                  </span>
                </div>
                {getPricing(739).savings && (
                  <p className="mb-3 text-sm font-medium text-green-400">
                    {getPricing(739).savings}
                  </p>
                )}
                <p className="text-sm text-slate-400">
                  {getPricingTier('business', 'description')}
                </p>
              </div>
              <ul className="mb-8 space-y-4 text-slate-300" role="list">
                {getTierRealFeatures('business').map((feature, i) => (
                  <li key={i} className="flex items-center">
                    <CheckCircle2 className="mr-3 size-5 text-green-400" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleGetStarted('business')}
                size="lg"
                className="mt-auto bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-xl hover:from-primary-700 hover:to-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {getPricingTier('business', 'button')}
              </Button>
            </motion.div>

            {/* Enterprise Tier */}
            <motion.div
              className="relative flex flex-col rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 shadow-xl backdrop-blur-sm transition-all duration-300 hover:border-primary-500/30 hover:shadow-2xl hover:shadow-primary-500/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="mb-6">
                <h3 className="mb-4 text-2xl font-semibold leading-[1.3] text-white">
                  {getPricingTier('enterprise', 'name')}
                </h3>
                <div className="mb-2">
                  <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-4xl font-bold text-transparent">
                    {getPricingTier('enterprise', 'price', {
                      price: getPricing(1470).price,
                    })}
                  </span>
                  <span className="text-lg font-medium text-slate-400">
                    {getPricing(1470).period}
                  </span>
                </div>
                {getPricing(1470).savings && (
                  <p className="mb-3 text-sm font-medium text-green-400">
                    {getPricing(1470).savings}
                  </p>
                )}
                <p className="text-sm text-slate-400">
                  {getPricingTier('enterprise', 'description')}
                </p>
              </div>
              <ul className="mb-8 space-y-4 text-slate-300" role="list">
                {getTierRealFeatures('enterprise').map((feature, i) => (
                  <li key={i} className="flex items-center">
                    <CheckCircle2 className="mr-3 size-5 text-green-400" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleGetStarted('enterprise')}
                size="lg"
                className="mt-auto bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg hover:from-primary-700 hover:to-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {getPricingTier('enterprise', 'button')}
              </Button>
            </motion.div>
          </motion.div>

          {/* Free Trial and Contact Sales Section - Side by Side */}
          <motion.div
            className="mx-auto mt-16 grid max-w-4xl gap-8 md:grid-cols-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {/* Free Trial Section - Left Side */}
            <div className="h-full text-center">
              <div className="flex h-full min-h-[180px] flex-col rounded-2xl border border-primary-500/30 bg-gradient-to-br from-primary-500/5 to-primary-600/5 p-8 backdrop-blur-sm">
                <h3 className="mb-3 text-xl font-semibold text-primary-400">
                  {getPricingTier('freeTrial', 'title')}
                </h3>
                <p className="mb-6 grow leading-relaxed text-slate-400">
                  {getPricingTier('freeTrial', 'subtitle')}
                </p>
                <Button
                  onClick={handleFreeTrial}
                  variant="outline"
                  size="lg"
                  className="mt-auto w-full border-2 border-primary-500 bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  {getPricingTier('freeTrial', 'button')}
                </Button>
              </div>
            </div>

            {/* Contact Sales Section - Right Side */}
            <div className="h-full text-center">
              <div className="flex h-full min-h-[180px] flex-col rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/60 to-slate-900/60 p-8 backdrop-blur-sm">
                <h3 className="mb-3 text-xl font-semibold text-slate-300">
                  {getPricingTier('custom', 'name')}
                </h3>
                <p
                  className="mb-6 grow leading-relaxed text-slate-400"
                  dangerouslySetInnerHTML={{
                    __html: getPricingTier('custom', 'customMessage'),
                  }}
                />
                <Button
                  onClick={handleContactSales}
                  variant="outline"
                  size="lg"
                  className="mt-auto w-full border-2 border-slate-600 bg-slate-800/50 text-white backdrop-blur-sm hover:bg-slate-700/50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  {getPricingTier('custom', 'button')}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section
        className="bg-gradient-to-br from-slate-900 to-slate-800 py-24"
        aria-labelledby="comparison-title"
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 id="comparison-title" className="py-1 text-4xl font-bold leading-[1.2] text-white">
              {getPricingComparison('title')}
            </h2>
          </motion.div>

          <motion.div
            className="overflow-x-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <table className="w-full min-w-[600px] table-auto border-collapse overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/80 to-slate-900/80 text-left shadow-2xl backdrop-blur-sm">
              <thead>
                <tr className="bg-slate-800/50">
                  <th className="border-b border-slate-700/50 p-6 text-lg font-semibold text-white">
                    {getPricingComparison('tableHeaders.features')}
                  </th>
                  <th className="border-b border-slate-700/50 p-6 text-center text-lg font-semibold text-white">
                    {getPricingComparison('tableHeaders.basic')}
                  </th>
                  <th className="border-b border-slate-700/50 p-6 text-center text-lg font-semibold text-white">
                    {getPricingComparison('tableHeaders.business')}
                  </th>
                  <th className="border-b border-slate-700/50 p-6 text-center text-lg font-semibold text-white">
                    {getPricingComparison('tableHeaders.enterprise')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-slate-800/30' : 'bg-slate-900/30'}>
                    <td className="border-b border-slate-700/30 px-6 py-4 font-medium text-slate-300">
                      {row.feature}
                    </td>
                    {(['basic', 'business', 'ent'] as const).map((tier, i) => (
                      <td key={i} className="border-b border-slate-700/30 px-6 py-4 text-center">
                        {typeof (row as any)[tier] === 'boolean' ? (
                          (row as any)[tier] ? (
                            <CheckCircle2
                              className="mx-auto size-6 text-green-400"
                              aria-label={getPricingAccessibility('included')}
                            />
                          ) : (
                            <span
                              className="mx-auto block text-xl text-slate-600"
                              aria-label={getPricingAccessibility('notIncluded')}
                            >
                              –
                            </span>
                          )
                        ) : (
                          <span className="font-medium text-slate-300">{(row as any)[tier]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="bg-slate-800/50 py-24" aria-labelledby="faq-title">
        <div className="container mx-auto px-6">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 id="faq-title" className="py-1 text-4xl font-bold leading-[1.2] text-white">
              {getPricingFAQ('title')}
            </h2>
          </motion.div>
          <div className="mx-auto max-w-4xl space-y-6">
            {faqItems.map((faq, idx) => (
              <motion.div
                key={idx}
                className="space-y-4 rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/60 to-slate-900/60 p-8 shadow-xl backdrop-blur-sm transition-all duration-300 hover:border-primary-500/30 hover:shadow-2xl hover:shadow-primary-500/10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <h3 className="text-xl font-semibold text-white">{faq.question}</h3>
                <p className="leading-relaxed text-slate-300">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 bg-slate-900 py-16" role="contentinfo">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
            {/* Company Info */}
            <div>
              <h4 className="mb-6 text-xl font-semibold text-white">
                {getLandingFooter('companyInfo')}
              </h4>
              <p className="mb-6 text-slate-400">
                {getLandingFooter('copyright', {
                  year: new Date().getFullYear(),
                })}
              </p>
            </div>

            {/* Empty middle column */}
            <div></div>

            {/* Links */}
            <div>
              <h4 className="mb-6 text-xl font-semibold text-white">
                {getLandingFooter('quickLinks')}
              </h4>
              <ul className="space-y-3 text-slate-400" role="list">
                <li>
                  <Link
                    to="/marketing"
                    className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    {getLandingFooter('links.home')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/marketing/product"
                    className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    {getLandingFooter('links.features')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/marketing/pricing"
                    className="rounded-md px-2 py-1 font-medium text-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                    aria-current="page"
                  >
                    {getLandingFooter('links.pricing')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    {getLandingFooter('links.login')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 border-t border-slate-700/50 pt-8 text-center text-slate-500">
            <Link
              to="/privacy"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.privacy')}
            </Link>
            <span className="mx-2">·</span>
            <Link
              to="/terms"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.terms')}
            </Link>
            <span className="mx-2">·</span>
            <Link
              to="/cookies"
              className="rounded-md px-2 py-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getLandingFooter('legal.cookies')}
            </Link>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialPurpose={modalPurpose}
      />
    </main>
  );
}
